import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiSolidEdit } from "react-icons/bi";
import { BsTrash3Fill } from "react-icons/bs";
import { FiDownload } from "react-icons/fi";
import { IoArrowBackSharp, IoReceiptSharp } from "react-icons/io5";
import { PiEyeDuotone } from "react-icons/pi";
import { withRouter } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Button, Col, Row, Table } from "reactstrap";
import swal from "sweetalert";
import {
  applyClassToCharacters,
  convertDataFromSnakeCaseToCamelCase,
  convertToDateString,
  getStringFromJSX,
  LoadingSpinner,
} from "../../constants/const";
import { encryptStorage } from "../../constants/EncryptStorage";
import { GetLanguageString, NoResult } from "../helper/Components";
import { GetCustomStyles, GetSelectedLanguage } from "../helper/Method";
import { ShowToast } from "../shared/ShowToast";
import { ApiFinance } from "./ApiFinance";
import { ApiFund } from "./ApiFund";
import EditFund from "./EditFund";
import EditOtherIncome from "./income/EditOtherIncome";
import EditOtherIncomeHelpFromMuqamiTanzeem from "./income/EditOtherIncomeHelpFromMuqamiTanzeem";
import EditOtherIncomeLoanFromMarkaz from "./income/EditOtherIncomeLoanFromMarkaz";
import EditOtherIncomeTarbiatiCourseReimbursment from "./income/EditOtherIncomeTarbiatiCourseReimbursment";
import ManageIncomeFilters from "./ManageIncomeFilters";

const ManageIncome = ({ history }) => {
  const { t } = useTranslation();
  const [selectedTransactionNo, setSelectedTransactionNo] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const [searchBtnClicked, setSearchBtnClicked] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const today = new Date();
  const selectedCategoryLabel =
    selectedTransaction?.category?.label1?.toLowerCase() || "";

  const [values, setValues] = useState({
    unit: { value: loggedInUnitId, lable: "" },
    unitId: loggedInUnitId,
    startDate: new Date(today.getFullYear(), today.getMonth(), 1),
    endDate: new Date(),
    bankAccountId: 0,
    selectedFinanceCategories: [],
    isSubUnits: true,
  });
  const handleSubUnits = (e) => {
    const { value } = e.target;
    let v = value;
    if (value === "true") v = true;
    else v = false;
    setValues({
      ...values,
      isSubUnits: v,
    });
  };
  const [transactionData, setTransactionData] = useState([]);
  const shouldShowRegistrationNumber =
    values.selectedFinanceCategories.length === 0 ||
    transactionData.some((transaction) => transaction.registrationNumber) ||
    values.selectedFinanceCategories.some(
      (category) => category.label1 === "infaq",
    );

  const handleSearch = (showLoader = true) => {
    setSearchBtnClicked(true);
    if (showLoader) {
      setLoading(true);
    }
    ApiFund()
      .GetInfaqTransactions({
        ...values,
        startDate: convertToDateString(values.startDate),
        endDate: convertToDateString(values.endDate),
      })
      .then((res) => {
        const convertedData = convertDataFromSnakeCaseToCamelCase(res.data);
        setTransactionData(convertedData);

        if (showLoader) {
          setLoading(false);
        }
      })
      .catch(() => {
        if (showLoader) {
          setLoading(false);
        }
      })
      .finally(() => {
        if (showLoader) {
          setLoading(false);
        }
      });
  };
  const handleOnDelete = (value, type) => {
    swal({
      title: t("common_delete_message"),
      text: t("common_delete_detail_message"),
      className: GetSelectedLanguage() === "en" ? "" : "ur",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        setLoading(true);
        ApiFund()
          .deleteFund(value, type)
          .then(() => {
            setLoading(false);
            setTransactionData((prev) =>
              prev.filter((transaction) => {
                const transactionValue =
                  type === "fund"
                    ? transaction.fundId
                    : transaction.transactionNo;
                return String(transactionValue) !== String(value);
              }),
            );
            handleSearch(false);
          })
          .catch((err) => {
            console.log(err);
            setLoading(false);
          });
      }
    });
  };

  const getAttachmentFile = (transNo) => {
    const toastId = toast.loading(t("fetching_attachment"));
    ApiFinance()
      .ViewTransactionAttachment(transNo)
      .then((res) => {
        try {
          const contentType =
            res.headers["content-type"] || "application/octet-stream";

          const blob = new Blob([res.data], { type: contentType });
          const fileURL = window.URL.createObjectURL(blob);

          window.open(fileURL, "_blank");

          setTimeout(() => {
            window.URL.revokeObjectURL(fileURL);
          }, 100);

          ShowToast(toastId, "attachment_fetched", "success");
        } catch (error) {
          console.error("Error opening attachment:", error);
          ShowToast(toastId, "error_opening_attachment", "error");
        }
      })
      .catch((err) => {
        console.error("Error fetching attachment:", err);
        ShowToast(toastId, "error_downloading_attachment", "error");
      });
  };
  const downloadAttachmentFile = (transNo) => {
    const toastId = toast.loading(t("downloading_attachment"));
    ApiFinance()
      .ViewTransactionAttachment(transNo)
      .then((res) => {
        try {
          const contentType =
            (res.headers &&
              (res.headers["content-type"] || res.headers["Content-Type"])) ||
            "application/octet-stream";
          const disposition =
            (res.headers &&
              (res.headers["content-disposition"] ||
                res.headers["Content-Disposition"])) ||
            "";

          // Try to extract filename from content-disposition header
          let fileName = "attachment";
          const fileNameMatch = disposition.match(
            /filename\*?=(?:UTF-8'')?["']?([^;"']+)/i,
          );
          if (fileNameMatch && fileNameMatch[1]) {
            try {
              fileName = decodeURIComponent(fileNameMatch[1]);
            } catch {
              fileName = fileNameMatch[1];
            }
          }

          // Determine extension from content type if filename has no extension
          const mime = contentType.split(";")[0].trim().toLowerCase();
          const extMap = {
            "application/pdf": "pdf",
            "image/png": "png",
            "image/jpeg": "jpg",
            "image/jpg": "jpg",
            "image/gif": "gif",
          };
          const ext = extMap[mime] || "bin";
          if (!/\.[a-z0-9]+$/i.test(fileName)) {
            fileName = `${fileName}.${ext}`;
          }

          const blob = new Blob([res.data], { type: contentType });
          const fileURL = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = fileURL;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Cleanup
          setTimeout(() => {
            window.URL.revokeObjectURL(fileURL);
          }, 100);

          ShowToast(toastId, "downloaded_attachment", "success");
        } catch (error) {
          console.error("Error downloading attachment:", error);
          ShowToast(toastId, "error_downloading_attachment", "error");
        }
      })
      .catch((err) => {
        console.error("Error fetching attachment:", err);
        ShowToast(toastId, "error_downloading_attachment", "error");
      });
  };

  const viewReceipt = (transactionNo, type) => {
    return new Promise((resolve, reject) => {
      const toastId = toast.loading(t("receipt_loading"));

      ApiFund()
        .GenerateReceipt(transactionNo, type)
        .then((res) => {
          const blob = new Blob([res.data], { type: "application/pdf" });
          const blobUrl = URL.createObjectURL(blob);

          // Open in new tab
          window.open(blobUrl, "_blank");

          // Clean up the object URL after the tab has had time to load it
          setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);

          toast.update(toastId, {
            render: getStringFromJSX(
              applyClassToCharacters(
                getStringFromJSX(
                  <GetLanguageString props={"receipt_opened"} />,
                ),
                "ur",
              ),
            ),
            type: "success",
            isLoading: false,
            autoClose: 3000,
          });
          resolve(true);
        })
        .catch((err) => {
          console.error(`Error viewing receipt: ${err}`);
          toast.update(toastId, {
            render: `Failed to open receipt: ${err.message}`,
            type: "error",
            isLoading: false,
            autoClose: 5000,
          });
          reject(err);
        });
    });
  };

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <div className="ibox ">
        <ToastContainer
          autoClose={3000}
          bodyClassName={GetSelectedLanguage()}
          closeOnClick
          pauseOnHover={false}
          draggable
        />
        <div className="ibox-title">
          <h5>{<GetLanguageString props="manage_income" />}</h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            {" "}
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>
        <div className="ibox-content">
          <ManageIncomeFilters
            values={values}
            setValues={setValues}
            handleSubUnits={handleSubUnits}
          />

          <Row>
            <Col md="12" className="text-center">
              <div style={{ marginTop: 10 }}>
                <Button
                  color="primary"
                  className="m-l-sm"
                  onClick={handleSearch}
                >
                  {<GetLanguageString props="common_search" />}
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </div>
      {searchBtnClicked && (
        <>
          {!loading ? (
            <div className="ibox">
              <div className="ibox-content">
                <>
                  {openEditModal && (
                    <>
                      {selectedTransaction?.fundId ? (
                        <EditFund
                          editValue={selectedTransactionNo}
                          setModal={setOpenEditModal}
                          unitId={values.unit.value}
                          onSuccess={handleSearch}
                        />
                      ) : selectedCategoryLabel === "loan_markaz" ? (
                        <EditOtherIncomeLoanFromMarkaz
                          transactionNo={selectedTransaction?.transactionNo}
                          transaction={selectedTransaction}
                          setModal={setOpenEditModal}
                          unitId={values.unit.value}
                          onSuccess={handleSearch}
                        />
                      ) : selectedCategoryLabel ===
                        "tarbiati_course_reimbursment_from_markaz" ? (
                        <EditOtherIncomeTarbiatiCourseReimbursment
                          transactionNo={selectedTransaction?.transactionNo}
                          transaction={selectedTransaction}
                          setModal={setOpenEditModal}
                          unitId={values.unit.value}
                          onSuccess={handleSearch}
                        />
                      ) : selectedCategoryLabel ===
                        "help_from_muqami_tanzeem" ? (
                        <EditOtherIncomeHelpFromMuqamiTanzeem
                          transactionNo={selectedTransaction?.transactionNo}
                          transaction={selectedTransaction}
                          setModal={setOpenEditModal}
                          unitId={values.unit.value}
                          onSuccess={handleSearch}
                        />
                      ) : (
                        <EditOtherIncome
                          transactionNo={selectedTransaction?.transactionNo}
                          transaction={selectedTransaction}
                          setModal={setOpenEditModal}
                          unitId={values.unit.value}
                          onSuccess={handleSearch}
                        />
                      )}
                    </>
                  )}
                  {transactionData.length === 0 ? (
                    <NoResult />
                  ) : (
                    <>
                      <Table>
                        <thead>
                          <tr style={{ borderBottom: "1px solid black" }}>
                            <th className="text-center">#</th>
                            {shouldShowRegistrationNumber ? (
                              <th className="text-center">
                                {
                                  <GetLanguageString props="member_report_rafiq_serial_number" />
                                }
                              </th>
                            ) : null}
                            <th className="text-center">
                              {<GetLanguageString props="funds_source" />}
                            </th>
                            <th className="text-center">
                              {
                                <GetLanguageString props="quick_reports3_unit_name" />
                              }
                            </th>

                            <th className="text-center">
                              {<GetLanguageString props="funds_date" />}
                            </th>

                            <th className="text-center">
                              {
                                <GetLanguageString props="funds_receipt_number" />
                              }
                            </th>

                            <th className="text-center">
                              {
                                <GetLanguageString props="funds_add_fund_amount" />
                              }
                            </th>
                            <th>
                              {<GetLanguageString props="funds_income_head" />}
                            </th>
                            <th className="text-center">
                              {<GetLanguageString props="funds_account" />}
                            </th>
                            <th className="text-center">
                              {<GetLanguageString props="transactions_memo" />}
                            </th>
                            <th
                              className="text-center"
                              style={{ minWidth: 70 }}
                            >
                              {<GetLanguageString props="common_action" />}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactionData.map((transaction, index) => (
                            <tr key={index}>
                              <td className="text-center">{index + 1}</td>
                              {shouldShowRegistrationNumber ? (
                                <td className="text-center">
                                  {transaction.registrationNumber || "-"}
                                </td>
                              ) : null}
                              <td className="text-center">
                                {applyClassToCharacters(
                                  transaction.memberName ||
                                    (transaction.category?.label1 ===
                                    "help_from_muqami_tanzeem"
                                      ? `${transaction.category?.label}${
                                          transaction.helpFromUnitName
                                            ? ` (${transaction.helpFromUnitName})`
                                            : ""
                                        }`
                                      : transaction.category?.label),
                                  "ur",
                                ) || "-"}
                              </td>
                              <td className="text-center ur">
                                {transaction.unitName}
                              </td>
                              <td className="text-center">
                                {moment(transaction.date, "YYYY-MM-DD").format(
                                  "DD/MM/YYYY",
                                )}
                              </td>

                              <td className="text-center">
                                {transaction.reference
                                  ?.trim()
                                  .split(/\s+/)
                                  .pop() || ""}
                              </td>

                              <td
                                className={"text-center "}
                                style={{
                                  color:
                                    transaction.amount < 0 ? "red" : "green",
                                }}
                              >
                                <strong>
                                  {new Intl.NumberFormat().format(
                                    transaction.amount,
                                  )}
                                </strong>
                              </td>
                              <td>{transaction?.accountName || "-"}</td>
                              <td className="text-center">
                                {transaction.bankName || "-"}
                              </td>
                              <td>{transaction.memo || "-"}</td>
                              {transaction.isSyncedWithFinanceSoftware ? (
                                <td className="text-center">
                                  <div>
                                    {transaction.fileId ? (
                                      <>
                                        <PiEyeDuotone
                                          color="#cf871bff"
                                          value={transaction.transactionNo}
                                          onClick={() =>
                                            getAttachmentFile(
                                              transaction.transactionNo,
                                            )
                                          }
                                          style={{
                                            outline: "none",
                                            cursor: "pointer",
                                          }}
                                          size={20}
                                        />
                                        &nbsp;&nbsp;&nbsp;
                                        <FiDownload
                                          color="#35A29F"
                                          value={transaction.transactionNo}
                                          onClick={() =>
                                            downloadAttachmentFile(
                                              transaction.transactionNo,
                                            )
                                          }
                                          style={{
                                            outline: "none",
                                            cursor: "pointer",
                                          }}
                                          size={20}
                                        />
                                      </>
                                    ) : (
                                      ""
                                    )}

                                    <>
                                      <BiSolidEdit
                                        color="#3588a2ff"
                                        style={{
                                          outline: "none",
                                          cursor: "pointer",
                                        }}
                                        size={20}
                                        onClick={() => {
                                          setSelectedTransaction(transaction);
                                          setSelectedTransactionNo(
                                            transaction.fundId ||
                                              transaction.transactionNo,
                                          );
                                          setOpenEditModal(true);
                                        }}
                                      />
                                      {(
                                        transaction?.accountTypeCode ?? ""
                                      ).toLowerCase() !== "amanat_account" &&
                                        transaction?.category?.label1?.toLowerCase() !==
                                          "help_from_muqami_tanzeem" && (
                                          <>
                                            &nbsp;&nbsp;&nbsp;
                                            <BsTrash3Fill
                                              color="#bd3826ff"
                                              style={{
                                                outline: "none",
                                                cursor: "pointer",
                                              }}
                                              size={20}
                                              onClick={() =>
                                                handleOnDelete(
                                                  transaction.fundId ??
                                                    transaction.transactionNo,
                                                  transaction.fundId
                                                    ? "fund"
                                                    : "transaction",
                                                )
                                              }
                                            />
                                          </>
                                        )}
                                      &nbsp;&nbsp;&nbsp;
                                      <IoReceiptSharp
                                        color="#5b7cfa"
                                        style={{
                                          outline: "none",
                                          cursor: "pointer",
                                        }}
                                        size={20}
                                        onClick={() =>
                                          viewReceipt(
                                            transaction.transactionNo,
                                            transaction.category?.label1?.toLowerCase() ===
                                              "infaq"
                                              ? "infaq"
                                              : "otherIncome",
                                          )
                                        }
                                      />
                                    </>
                                  </div>
                                </td>
                              ) : (
                                <td className="text-center">-</td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}
                </>
              </div>
            </div>
          ) : (
            LoadingSpinner()
          )}
        </>
      )}
    </div>
  );
};

export default withRouter(ManageIncome);
